import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { MarginTradingRemote } from "@modules/margin-trading/services/margin-trading.remote";
import {
	AssetOrderType,
	AssetSort,
	PageInfo,
	SortOrder,
	TimePeriod
} from "../../../api/graphql/bff/generated/types.graphql-gen";
import { Timespan } from "@helpers/const/timespan";
import { AssetFragment } from "../../../api/graphql/bff/generated/fragments.service.graphql-gen";
import { Tradable } from "@models/interfaces/tradable";

@Injectable({
	providedIn: "root"
})
export class MarginTradingDiscoveryFacade {
	private readonly statsigService = inject(StatsigClientService);
	private readonly bff: MarginTradingRemote = inject(MarginTradingRemote);

	private _marginAssets: WritableSignal<AssetFragment[]> = signal([]);
	private _marginPagination: WritableSignal<PageInfo> = signal(null);

	marginAssets = this._marginAssets.asReadonly();
	marginPagination = this._marginPagination.asReadonly();

	private sortingChanged: boolean = false;

	marginTradingEnabled(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_ENABLED);
	}

	leveragedTokensEnabled(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.LEVERAGE);
	}

	getMarginAssets(
		timeframeValue: Timespan,
		sortBy: AssetSort = AssetSort.MarketCap,
		sortOrder: SortOrder = SortOrder.Desc,
		after?: string
	): Observable<
		Extract<
			AssetFragment,
			{
				__typename?: "CryptoAsset";
			}
		>[]
	> {
		if (!after && this.marginPagination()?.hasNextPage && !this.sortingChanged) {
			after = this.marginPagination()?.endCursor;
		}
		return this.bff
			.getAssets({
				input: {
					filters: {
						orderTypes: [AssetOrderType.MarginLong],
						settings: {
							includeHiddenFromDiscovery: false
						}
					},
					sortBy,
					sortOrder
				},
				after,
				period: this.convertTimeFrametoTimePeriod(timeframeValue),
				includeOHLC: false,
				includeMaximumLeverage: true
			})
			.pipe(
				tap((response) => this._marginPagination.set(response.pageInfo)),
				tap(() => (this.sortingChanged = false)),
				map((assets) =>
					assets.edges.map(
						(edge) =>
							edge.node as Extract<
								AssetFragment,
								{
									__typename?: "CryptoAsset";
								}
							>
					)
				),
				tap((assets) => this._marginAssets.update((oldAssets) => (after ? [...oldAssets, ...assets] : assets)))
			);
	}

	getMarginAsset(pid: string): Observable<AssetFragment> {
		return this.bff.getAsset({
			input: { pid },
			includeOHLC: false
		});
	}

	public isTradableMarginAsset(tradable: Tradable): boolean {
		return this._marginAssets().some(
			(marginAsset) => marginAsset.pid === tradable.getPid() && marginAsset.marginLongSettings.buyActive
		);
	}

	public setSortingChanged(): void {
		this.sortingChanged = true;
	}

	private convertTimeFrametoTimePeriod(timeframe: Timespan): TimePeriod {
		switch (timeframe) {
			case Timespan.Day:
				return TimePeriod.Day;
			case Timespan.Week:
				return TimePeriod.Week;
			case Timespan.Month:
				return TimePeriod.Month;
			case Timespan.Year:
				return TimePeriod.Year;
			default:
				return TimePeriod.Day; // Default case if no match
		}
	}
}
